import { Route } from '@angular/router';
import { LayoutComponent } from '@app/_shared/components/layout/layout.component';
import { StripeCheckoutComponent } from '@app/features/stripe-checkout/stripe-checkout.component';
import { AppStateGuard } from '@core/guards/app-state.guard';
import { asyncOrderGuard } from '@core/guards/async-order.guard';
import { AuthGuard } from '@core/guards/auth.guard';
import { BlockedPaywallGuard } from '@core/guards/blocked-paywall.guard';
import { CheckoutGuard } from '@core/guards/checkout.guard';
import { IsLoggedGuard } from '@core/guards/is-logged.guard';
import {
  legacyActionOnDashboardGuard,
  legacyAppLinkGuard,
} from '@core/guards/legacy-app-link.guard';
import { QuizGuard } from '@core/guards/quiz.guard';
import { ResetPasswordGuard } from '@core/guards/reset-password.guard';
import { warmUpChartLibrary } from '@core/utils/chart-library.utils';

export const appRoutes: Route[] = [
  {
    path: '',
    component: LayoutComponent,
    canActivate: [IsLoggedGuard, ResetPasswordGuard],
    data: {
      layout: 'empty',
      state: 'auth',
    },
    loadChildren: () => import('@app/features/auth/auth.routes'),
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [
      asyncOrderGuard([AuthGuard, BlockedPaywallGuard, AppStateGuard]),
    ],
    data: {
      state: 'app',
    },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'accounts',
        data: {
          name: 'accounts',
          // R19. Preloaded at idle, same reasoning as transactions.
          preload: true,
          // Net worth draws a chart; fetch the library right after the chunk (QA2 2026-09-10).
          warmUp: warmUpChartLibrary,
        },
        loadChildren: () =>
          import('@app/features/accounts/_components/accounts.routes'),
      },
      {
        path: 'budgets',
        data: {
          name: 'budgets',
          // QA2 2026-09-10. Opened from the dashboard card; the first click paid two sequential
          // chunk round-trips (350–420 ms on my-dev). 169 kB gzip at idle in the production build.
          preload: true,
        },
        loadChildren: () => import('@app/features/budgets/budgets.routes'),
      },
      {
        path: 'dashboard',
        // POC-8830: every legacy deep link ends here with its `action` intact.
        canActivate: [legacyActionOnDashboardGuard],
        data: {
          name: 'dashboard',
          // R19. Marked too, for a session that deep-links elsewhere first.
          preload: true,
        },
        loadChildren: () => import('@app/features/dashboard/dashboard.routes'),
      },
      {
        path: 'debt-plan',
        data: {
          name: 'debt-plan',
          // QA2 2026-09-10. Sidebar item; first click paid 350 ms of chunk round-trips. 60 kB gzip.
          preload: true,
        },
        loadChildren: () =>
          import('@app/features/debt-payoff/debt-payoff-plan.routes'),
      },
      {
        path: 'goals',
        data: {
          name: 'goals',
          // QA2 2026-09-10. Sidebar item; first click paid 383 ms of chunk round-trips. 121 kB gzip.
          preload: true,
        },
        loadChildren: () => import('@app/features/goals/goals.routes'),
      },
      {
        path: 'insights',
        data: {
          name: 'insights',
          // R19. Preloaded at idle, on the developer's read of where people go.
          preload: true,
          // The spending chart needs the library; fetch it right after the chunk (QA2 2026-09-10).
          warmUp: warmUpChartLibrary,
        },
        loadChildren: () => import('@app/features/insights/insights.routes'),
      },
      {
        // POC-8665. The pre-2.0 report, kept reachable as "Classic Insights"
        // while Insights 2.0 rolls out. Not in the sidebar, not preloaded.
        path: 'insights-old',
        data: {
          name: 'insights-old',
          warmUp: warmUpChartLibrary,
        },
        loadChildren: () =>
          import('@app/features/insights-old/insights-old.routes'),
      },
      {
        path: 'categories',
        data: {
          name: 'categories',
        },
        loadChildren: () =>
          import('@app/features/categories/categories.routes'),
      },
      {
        path: 'hashtags',
        data: {
          name: 'hashtags',
        },
        loadChildren: () => import('@app/features/hashtags/hashtags.routes'),
      },
      {
        path: 'merchants',
        data: {
          name: 'merchants',
        },
        loadChildren: () => import('@app/features/merchants/merchants.routes'),
      },
      {
        path: 'plan',
        data: {
          name: 'plan',
          // QA2 2026-09-10. Sidebar item; first click paid 418 ms of chunk round-trips and then
          // 567–911 ms more for the chart library. 140 kB gzip, plus the library via warmUp.
          preload: true,
          warmUp: warmUpChartLibrary,
        },
        loadChildren: () => import('@app/features/plan/plan.routes'),
      },
      {
        path: 'payments',
        data: {
          name: 'regular-payments',
        },
        loadChildren: () =>
          import('@app/features/regular-payments/regular-payments.routes'),
      },
      {
        path: 'transactions',
        data: {
          name: 'transactions',
          viewFrom: 'transactions',
          // R19. Preloaded at idle: the likeliest screen after the dashboard.
          preload: true,
        },
        loadChildren: () =>
          import('@app/features/transactions/transactions.routes'),
      },
      {
        path: 'settings',
        data: {
          name: 'settings',
        },
        loadChildren: () => import('@app/features/settings/settings.routes'),
      },
      {
        path: 'support',
        data: {
          name: 'support',
        },
        loadChildren: () => import('@app/features/support/support.routes'),
      },
    ],
  },
  {
    path: 'start',
    component: LayoutComponent,
    data: {
      state: 'quiz',
    },
    canActivate: [asyncOrderGuard([AuthGuard, QuizGuard])], // [AuthGuard],
    children: [
      { path: '', redirectTo: 'quiz', pathMatch: 'full' },
      {
        path: 'quiz',
        loadChildren: () => import('@app/features/quiz/quiz.routes'),
      },
      {
        path: 'support',
        loadChildren: () => import('@app/features/support/support.routes'),
      },
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
      state: 'blocked',
    },
    children: [
      {
        path: 'blocked',
        canActivate: [BlockedPaywallGuard],
        loadComponent: () =>
          import('@app/features/blocked-paywall/blocked-paywall.component').then(
            (m) => m.BlockedPaywallComponent
          ),
      },
    ],
  },
  {
    // Deliberately outside the guarded auth block: the emailed link must open
    // for signed-in and signed-out users alike. loadComponent, because the
    // eager import used to drag this auth component into the initial bundle.
    path: 'validationemail/:hash',
    pathMatch: 'full',
    loadComponent: () =>
      import('@app/features/auth/_common/components/email-verification/email-verification.component').then(
        (m) => m.EmailVerificationComponent
      ),
  },
  {
    path: 'stripeCheckout',
    canActivate: [CheckoutGuard],
    component: StripeCheckoutComponent,
  },
  {
    // Legacy-format deep links (the Stripe portal's return among them) —
    // decoded and forwarded by the guard; see legacyAppLinkGuard.
    path: 'app',
    canActivate: [legacyAppLinkGuard],
    children: [],
  },
  { path: '**', redirectTo: 'dashboard' },
];
